import { Injectable, EventEmitter } from '@angular/core';
import { DateService, LogService } from '@oa/shared-core';

@Injectable({
  providedIn: "root",
})
export class ExitIntentService {
  private _serviceName = 'ExitIntentService';
  private _scrollStack = [];
  private _disabled: boolean;

  /**
   * The number of items that we consider in the scroll stack.
   * If more items than _maxScrollStackSize are are added to the stack,
   * the oldest ones are removed.
   */
  private _maxScrollStackSize = 25;

  /**
   * Another field that controls the content of the _scrollStack items.  Anything items in _scrollStack that are older than this field, are removed.
   */
  private _scrollStackMaxItemAgeMilliseconds = 1200;

  /**
   * Field that describes the number of consecutive scroll up events
   */
  private _consecutiveScrollUp = 0;

  /**
   * Field that describes the number of consecutive scroll down events
   */
  private _consecutiveScrollDown = 0;

  /**
   * The scroll distance between the first item in the _scrollStack and the last.
   */
  private _scrollStackSpan = 0;

  /**
   * The amount of scroll distance that must occur between the first and last items in the scroll stack
   * before an exit intent event is emitted. Default is 100.
   */
  private _scrollStackSpanThreshold = 100;

  /**
   * The key name used to store the last exit intent event
   */
  private _exitIntentLastEmitKeyName = 'exitIntentLastEmit';

  /**
   * Fires when scrolling up is detected
   */
  public scrollUp = new EventEmitter();

  /**
   * Fires when scrolling down is detected
   */
  public scrollDown = new EventEmitter();

  /**
   * Fires when an exit intent is detected
   */
  public exitIntent = new EventEmitter<any>();

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    return this.log.verboseLogging;
  }

  /**
   * Disabled flag . When true, not exit intent messages are not shown.
   *  */
  public get disabled(): boolean {
    return this._disabled;
  }
  public set disabled(v: boolean) {
    this._disabled = v;

    if (this.verboseLogging) {
      console.log(`${this._serviceName}.disabled set v=`, v);
    }
  }

  constructor(private dateHelper: DateService, private log: LogService) {}

  public get exitIntentLastEmit(): Date {
    const s = localStorage.getItem(this._exitIntentLastEmitKeyName);
    if (s) {
      return new Date(s);
    } else {
      return null;
    }
  }

  public set exitIntentLastEmit(v: Date) {
    if (v) {
      localStorage.setItem('exitIntentLastEmit', v.toISOString());
    } else {
      localStorage.removeItem(this._exitIntentLastEmitKeyName);
    }
  }

  /**
   * Adds an item to the scroll stack
   * @param item
   */
  public add(item: { timestamp: Date; pos: number }) {
    this._scrollStack.push(item);

    // purge anything out of the scroll stack that no longer belongs
    this.clearStaleScrollStackItems();

    this.recalculate();
  }

  /**
   * Calculates new metrics and raises any events that need raising
   */
  private recalculate() {
    if (this._scrollStack && this._scrollStack.length > 2) {
      let firstItem = this._scrollStack[0];

      let lastItem = this._scrollStack[this._scrollStack.length - 1];

      let nextToLastItem = this._scrollStack[this._scrollStack.length - 2];

      let Δ = nextToLastItem.pos - lastItem.pos;

      this._scrollStackSpan = firstItem.pos - lastItem.pos;

      if (Δ > 0) {
        // we are scrolling up!
        this.onScrollUp(firstItem, lastItem, Δ);
      } else if (Δ < 0) {
        // we are scrolling down  !
        this.onScrollDown(firstItem, lastItem, Δ);
      }

      if (this.log.verboseLogging) {
        console.log(`${this._serviceName}.recalculate() firstItem=`, firstItem);
        console.log(`${this._serviceName}.recalculate() nextToLastItem=`, nextToLastItem);
        console.log(`${this._serviceName}.recalculate() lastItem=`, lastItem);
        console.log(`${this._serviceName}.recalculate() _scrollStackSpan=`, this._scrollStackSpan);
      }
    }
  }

  /**
   *  Handles the logic when a scroll down is detected
   * @param firstItem
   * @param lastItem
   * @param Δ
   */
  private onScrollDown(firstItem: any, lastItem: any, Δ: number) {
    if (this.log.verboseLogging) {
      console.log(`${this._serviceName}.onScrollDown`);
    }

    // The normal scroll property sets w/ scrollDown emit
    this._consecutiveScrollDown = this._consecutiveScrollDown + 1;
    this._consecutiveScrollUp = 0;

    this.scrollDown.emit();

    // we do nothing else on scroll down

    if (this.log.verboseLogging) {
      console.log(
        `${this._serviceName}.onScrollDown _consecutiveScrollUp=${this._consecutiveScrollUp},_consecutiveScrollDown=${this._consecutiveScrollDown}`
      );
    }
  }

  /**
   * Handles the logic when a scroll up is detected
   * @param firstItem
   * @param lastItem
   * @param Δ delta
   */
  private onScrollUp(firstItem: any, lastItem: any, Δ: number) {
    if (this.log.verboseLogging) {
      console.log(`${this._serviceName}.onScrollUp`);
    }

    // The normal scroll property sets w/ scrollUp emit
    this._consecutiveScrollUp = this._consecutiveScrollUp + 1;
    this._consecutiveScrollDown = 0;
    this.scrollUp.emit();

    // But is this an exit attempt?
    if (this._scrollStackSpan > this._scrollStackSpanThreshold) {
      // the threshold has been reached.

      //but first, check to see if we have already emitted
      if (this.exitIntentLastEmit) {
        const millisecondsSinceLastEmit = this.dateHelper.getMillisecondsBetween(
          this.dateHelper.nowUTCMilliseconds,
          this.exitIntentLastEmit
        );

        if (this.log.verboseLogging) {
          console.log(`${this._serviceName}.onScrollUp millisecondsSinceLastEmit=`, millisecondsSinceLastEmit);
        }
      } else {
        this.exitIntent.emit({
          scrollItem: lastItem,
        });

        if (this.log.verboseLogging) {
          console.log(`${this._serviceName}.onScrollUp Emitted exitIntent`);
        }

        this.exitIntentLastEmit = this.dateHelper.nowUTCMilliseconds;
      }
    }

    if (this.log.verboseLogging) {
      console.log(
        `${this._serviceName}.onScrollUp _consecutiveScrollUp=${this._consecutiveScrollUp},_consecutiveScrollDown=${this._consecutiveScrollDown}`
      );
    }
  }

  /**
   * Clears old (or excessive) scroll stack items
   */
  private clearStaleScrollStackItems() {
    let i = 0;

    let arr = [];

    let d2 = this.dateHelper.nowUTCMilliseconds;

    for (let item of Array.from(this._scrollStack).reverse()) {
      if (i <= this._maxScrollStackSize) {
        const ms = this.dateHelper.getMillisecondsBetween(d2, item.timestamp);

        if (ms < this._scrollStackMaxItemAgeMilliseconds) {
          arr.push(item);
          i = i + 1;
        }
      } else {
        break;
      }
    }

    this._scrollStack = arr.reverse();

    if (this.log.verboseLogging) {
      console.log(`${this._serviceName}.clearStaleScrollStackItems() `, this._scrollStack);
    }
  }

  private exitItent: any;

  
}
